import { REPORT_GRID_COLUMNS } from '../constants';
import { ReportRowCell } from '../types';

/**
 * Snap rules for the header/footer grid.
 *
 * The designer lets the user drop an element anywhere on the sheet and then
 * snaps it to a placement the renderer can print. These rules are defined here,
 * next to the renderer, because the renderer is the authority on what is
 * printable — and it applies them again when loading a template, so a stored
 * layout can never make it to paper in a state the renderer cannot lay out.
 */

/** Column index (0-based) a horizontal drop position falls into. */
export const snapColumnIndex = (
  offsetRatio: number,
  columns: number = REPORT_GRID_COLUMNS
): number => {
  if (!Number.isFinite(offsetRatio)) {
    return 0;
  }

  const index = Math.floor(offsetRatio * columns);

  return Math.min(Math.max(index, 0), columns - 1);
};

/**
 * Cells with their spans made valid: whole numbers, at least one column each,
 * and never wider than the grid in total.
 *
 * Cells beyond what the grid can hold are dropped rather than silently squeezed
 * to zero width — an element that cannot be placed must not pretend it was.
 */
export const snapGridRow = (
  cells: ReportRowCell[],
  columns: number = REPORT_GRID_COLUMNS
): ReportRowCell[] => {
  const placeable = cells.slice(0, columns);
  const spans = placeable.map((cell) => {
    const span = Math.round(cell.span ?? 0);

    return Math.min(Math.max(span, 1), columns);
  });

  const total = spans.reduce((sum, span) => sum + span, 0);

  if (total <= columns) {
    // Grow the last cell so the row fills the width instead of leaving a gap.
    const spare = columns - total;
    return placeable.map((cell, index) => ({
      ...cell,
      span:
        index === placeable.length - 1 ? spans[index] + spare : spans[index],
    }));
  }

  // Over the grid width: scale down proportionally, keeping every cell at 1+.
  const scaled = spans.map((span) =>
    Math.max(Math.floor((span / total) * columns), 1)
  );
  let overflow =
    scaled.reduce((sum, span) => sum + span, 0) - columns;

  for (let index = scaled.length - 1; index >= 0 && overflow > 0; index -= 1) {
    const reducible = Math.min(scaled[index] - 1, overflow);
    scaled[index] -= reducible;
    overflow -= reducible;
  }

  return placeable.map((cell, index) => ({ ...cell, span: scaled[index] }));
};

/** Total columns a row occupies once snapped. */
export const sumRowSpans = (cells: ReportRowCell[]): number =>
  cells.reduce((total, cell) => total + (cell.span ?? 0), 0);
